import { IProgram } from "../../interfaces/models/IEvent";
import { MemberEventAttendee } from "../../interfaces/models/IMemberEventAttendee";
import { IRegistration } from "../../interfaces/models/IRegistration";

export type AttendeesLoadState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "success"; data: MemberEventAttendee[] }
  | { status: "error"; error: string };

export type RegistrationLoadState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "success"; data: IRegistration | null }
  | { status: "error"; error: string };

type AttendeesCacheEntry = {
  promise?: Promise<MemberEventAttendee[]>;
  data?: MemberEventAttendee[];
  error?: string;
};

type RegistrationCacheEntry = {
  promise?: Promise<IRegistration | null>;
  data?: IRegistration | null;
  error?: string;
};

type EnrichedProgramsCacheEntry = {
  sourceKey: string;
  programs: IProgram[];
};

const attendeesCache = new Map<string, AttendeesCacheEntry>();
const registrationCache = new Map<string, RegistrationCacheEntry>();
const enrichedProgramsCache = new Map<string, EnrichedProgramsCacheEntry>();

export function getAttendeesCacheEntry(eventId: string): AttendeesCacheEntry {
  const existing = attendeesCache.get(eventId);
  if (existing) {
    return existing;
  }

  const entry: AttendeesCacheEntry = {};
  attendeesCache.set(eventId, entry);
  return entry;
}

export function setAttendeesCacheSuccess(
  eventId: string,
  data: MemberEventAttendee[],
): void {
  const entry = getAttendeesCacheEntry(eventId);
  entry.data = data;
  entry.error = undefined;
  entry.promise = undefined;
}

export function setAttendeesCacheError(eventId: string, error: string): void {
  const entry = getAttendeesCacheEntry(eventId);
  entry.error = error;
  entry.data = undefined;
  entry.promise = undefined;
}

export function setAttendeesCachePromise(
  eventId: string,
  promise: Promise<MemberEventAttendee[]>,
): void {
  const entry = getAttendeesCacheEntry(eventId);
  entry.promise = promise;
}

export function getRegistrationCacheEntry(
  cacheKey: string,
): RegistrationCacheEntry {
  const existing = registrationCache.get(cacheKey);
  if (existing) {
    return existing;
  }

  const entry: RegistrationCacheEntry = {};
  registrationCache.set(cacheKey, entry);
  return entry;
}

export function setRegistrationCacheSuccess(
  cacheKey: string,
  data: IRegistration | null,
): void {
  const entry = getRegistrationCacheEntry(cacheKey);
  entry.data = data;
  entry.error = undefined;
  entry.promise = undefined;
}

export function setRegistrationCacheError(
  cacheKey: string,
  error: string,
): void {
  const entry = getRegistrationCacheEntry(cacheKey);
  entry.error = error;
  entry.data = undefined;
  entry.promise = undefined;
}

export function setRegistrationCachePromise(
  cacheKey: string,
  promise: Promise<IRegistration | null>,
): void {
  const entry = getRegistrationCacheEntry(cacheKey);
  entry.promise = promise;
}

export function getEnrichedProgramsFromCache(
  eventId: string,
  sourceKey: string,
): IProgram[] | null {
  const entry = enrichedProgramsCache.get(eventId);
  if (!entry || entry.sourceKey !== sourceKey) {
    return null;
  }
  return entry.programs;
}

export function setEnrichedProgramsCache(
  eventId: string,
  sourceKey: string,
  programs: IProgram[],
): void {
  enrichedProgramsCache.set(eventId, { sourceKey, programs });
}

export function clearEventDetailCache(eventId: string): void {
  attendeesCache.delete(eventId);
  enrichedProgramsCache.delete(eventId);

  for (const key of Array.from(registrationCache.keys())) {
    if (key.startsWith(`${eventId}:`)) {
      registrationCache.delete(key);
    }
  }
}

export function attendeesStateFromCache(eventId: string): AttendeesLoadState {
  const entry = attendeesCache.get(eventId);
  if (!entry) {
    return { status: "idle" };
  }
  if (entry.promise) {
    return { status: "loading" };
  }
  if (entry.error) {
    return { status: "error", error: entry.error };
  }
  if (entry.data) {
    return { status: "success", data: entry.data };
  }
  return { status: "idle" };
}

export function registrationStateFromCache(
  cacheKey: string,
): RegistrationLoadState {
  const entry = registrationCache.get(cacheKey);
  if (!entry) {
    return { status: "idle" };
  }
  if (entry.promise) {
    return { status: "loading" };
  }
  if (entry.error) {
    return { status: "error", error: entry.error };
  }
  if (entry.data !== undefined) {
    return { status: "success", data: entry.data };
  }
  return { status: "idle" };
}
